import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { CopyValue } from '../../../../shared/sso-workspace/copy-value'
import { formatDateTime } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getTimeline, parkEvent, replayEvent, resendDelivery } from '../../../../api-new/state/notify'
import {
  categoryLabel, channelLabel, deliveryStatusLabel, deliveryStatusTone, eventStatusLabel, eventStatusTone,
  outcomeLabel, outcomeTone, priorityLabel, providerLabel
} from '../../../../shared/notify/words'
import { useOrganizationName } from '../../../../shared/org-search/use-organization-names'
import { ReasonDialog } from './reason-dialog'
import { RESENDABLE } from './delivery-log'
import { useNotifyOpsAccess } from './access'
import { NOTIFY_BASE, ORG_ACTIVITY_BASE, capturePath } from './paths'
import './notify.scss'

const GONE = "This notification isn't available. It may be older than the retention period, or not one of your organization's."

// Each C-4 TimelineStep kind in words, with its dot's tone.
const STEP = {
  raised: { title: 'Raised' },
  relayed: { title: 'Relayed' },
  deduped: { title: 'Duplicate, not sent again' },
  skipped: { title: 'Skipped', tone: 'warn' },
  parked: { title: 'Parked', tone: 'warn' },
  replayed: { title: 'Replayed' },
  resolved: { title: 'Recipients found' },
  delivery_planned: { title: 'Delivery planned' },
  delivery_skipped: { title: 'Delivery skipped', tone: 'warn' },
  attempt: { title: 'Attempt' },
  sent: { title: 'Sent', tone: 'ok' },
  captured: { title: 'Captured, not sent', tone: 'ok' },
  retrying: { title: 'Retrying', tone: 'warn' },
  failed: { title: 'Failed', tone: 'bad' },
  suppressed: { title: 'Suppressed', tone: 'bad' },
  expired: { title: 'Expired', tone: 'bad' },
  resent: { title: 'Resent' },
  read: { title: 'Read', tone: 'ok' },
  archived: { title: 'Archived' }
}
export const stepWords = kind => STEP[kind] || { title: kind || 'Step' }

const ms = v => (v === null || v === undefined ? '' : `${v} ms`)

const OrgName = ({ id, name }) => {
  const looked = useOrganizationName(name ? null : id)
  if (!id) return <span className="sw-hint">None</span>
  return name || looked || `Organization ${id}`
}

// One notification's whole path (C-4 GET /notify/timeline/:id, id = the
// trace id, or an outbox id for every recipient of one event): raised
// (outbox) -> relayed (event) -> resolved -> each delivery -> each attempt
// -> the provider's response -> its capture. The staff view (`system`)
// also shows who raised it, the request, the raw provider status and the
// captures, and may replay or park the event and resend a delivery; the
// organization's view shows none of those (the server leaves them out).
export const TimelineView = ({ traceID, system = false }) => {
  const access = useNotifyOpsAccess()
  const canAct = system && access.canUpdate
  const [timeline, setTimeline] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [reload, setReload] = useState(0)
  const [action, setAction] = useState(null) // { kind: 'resend' | 'replay' | 'park', delivery? }
  const [busy, setBusy] = useState(false)
  const [actionError, actionFailure, setActionError] = useErrorLine()
  const [notice, setNotice] = useState(null)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    getTimeline(traceID, system ? 'system' : 'organization').then(
      t => {
        if (!live.current) return
        setTimeline(t || null)
        setError(null)
      },
      err => {
        if (!live.current) return
        setTimeline(null)
        setError(err && err.status === 404 ? GONE : serverMessage(err, "This timeline couldn't be loaded."), err && err.status === 404 ? null : err)
      }
    )
  }, [traceID, system, reload, setError])

  const back = system ? `${NOTIFY_BASE}/deliveries` : ORG_ACTIVITY_BASE
  const emailBase = system ? '/admin/email-activity' : '/org/email-activity'

  const run = async reason => {
    if (!action) return
    setBusy(true)
    setActionError(null)
    try {
      if (action.kind === 'resend') {
        const d = await resendDelivery(action.delivery.id, reason)
        setNotice(`Delivery ${action.delivery.id} was sent again as delivery ${(d && d.id) || 'new'}.`)
      } else if (action.kind === 'replay') {
        await replayEvent(timeline.event.id, reason)
        setNotice('The event is being processed again. Deliveries already made are not repeated.')
      } else {
        await parkEvent(timeline.event.id, reason)
        setNotice("The event is parked: it won't be retried.")
      }
      setAction(null)
      setReload(n => n + 1)
    } catch (err) {
      setActionError(serverMessage(err, "That couldn't be done."), err)
    } finally {
      setBusy(false)
    }
  }

  const header =
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
      <Link to={back}>Back to the delivery log</Link>
    </div>

  if (timeline === undefined) return <>{header}<p className="sw-hint" role="status">Loading…</p></>
  if (timeline === null) {
    return (
      <>
        {header}
        <p className="text-danger small" role="alert">{error}</p>
        <ErrorReference error={failure} />
      </>
    )
  }

  const event = timeline.event || {}
  const notifications = timeline.notifications || []
  const deliveries = timeline.deliveries || []
  const attempts = timeline.attempts || []
  const captures = system ? timeline.captures || [] : []
  const steps = timeline.steps || []
  const captureOf = deliveryID => captures.find(c => c.deliveryId === deliveryID)

  return (
    <div data-testid="notify-timeline">
      {header}
      {notice && <p className="text-success small" role="status">{notice}</p>}

      <FleetSection id="nf-tl-head" title="Notification"
        actions={canAct && event.id &&
          <>
            <button type="button" className="btn btn-sm sw-btn" onClick={() => { setActionError(null); setAction({ kind: 'replay' }) }}>Replay event</button>
            {event.status !== 'parked' && <button type="button" className="btn btn-sm sw-btn" onClick={() => { setActionError(null); setAction({ kind: 'park' }) }}>Park event</button>}
          </>}>
        <dl className="sw-dl">
          <dt>Trace id</dt>
          <dd><span className="sw-mono">{timeline.traceId || traceID}</span> <CopyValue value={timeline.traceId || traceID} label="trace id" /></dd>
          {event.type && <><dt>Type</dt><dd className="sw-mono">{event.type}</dd></>}
          {system && <><dt>Organization</dt><dd><OrgName id={event.organizationId} name={event.organizationName} /></dd></>}
          {event.status && <><dt>Event</dt><dd><Pill tone={eventStatusTone(event.status)} title={event.failedReason || undefined}>{eventStatusLabel(event.status)}</Pill>{event.replayCount > 0 && <span className="sw-hint ms-2">{`replayed ${event.replayCount}×`}</span>}</dd></>}
          {event.createdAt && <><dt>Raised</dt><dd>{formatDateTime(event.createdAt)}</dd></>}
          {system && event.actorLabel && <><dt>Raised by</dt><dd>{event.actorLabel}{event.impersonatorLoginId ? <span className="sw-hint">{` (impersonated by login ${event.impersonatorLoginId})`}</span> : null}</dd></>}
          {system && event.requestId && <><dt>Request</dt><dd><Link className="sw-mono" to={`/admin/request-log/${encodeURIComponent(event.requestId)}`}>{event.requestId}</Link></dd></>}
          {typeof event.recipients === 'number' && <><dt>Recipients</dt><dd>{event.recipients}</dd></>}
        </dl>
      </FleetSection>

      <FleetSection id="nf-tl-steps" title="What happened">
        {steps.length === 0 && <p className="sw-hint m-0">Nothing has happened yet.</p>}
        {steps.length > 0 &&
          <ol className="nf-steps" aria-label="Timeline">
            {steps.map((s, i) => {
              const words = stepWords(s.kind)
              const capture = s.captureId && captures.find(c => c.id === s.captureId)
              return (
                <li key={`${s.at}-${s.kind}-${i}`} data-tone={words.tone || undefined}>
                  <span className="nf-step-title">{words.title}</span>
                  <span className="sw-hint nf-step-when">{formatDateTime(s.at)}</span>
                  {s.summary && <div>{s.summary}</div>}
                  {s.deliveryId && <div className="sw-hint small">{`Delivery ${s.deliveryId}${s.attemptId ? ` · attempt ${s.attemptId}` : ''}`}</div>}
                  {capture && <div><Link to={capturePath(capture.id)}>{`Open capture ${capture.id}`}</Link></div>}
                  {system && s.detail && Object.keys(s.detail).length > 0 && <pre className="nf-json mt-1" aria-label="Step detail">{JSON.stringify(s.detail, null, 2)}</pre>}
                </li>
              )
            })}
          </ol>}
      </FleetSection>

      {notifications.length > 0 &&
        <FleetSection id="nf-tl-recipients" title={notifications.length === 1 ? 'Recipient' : `Recipients (${notifications.length})`}>
          <div className="table-responsive">
            <table className="table nf-table" aria-label="Recipients">
              <thead><tr><th>Login</th><th>Email</th><th>Title</th><th>Category</th><th>Priority</th><th>Read</th></tr></thead>
              <tbody>
                {notifications.map(n =>
                  <tr key={n.id}>
                    <td className="sw-mono">{n.loginId || ''}</td>
                    <td>{n.recipientEmail || ''}</td>
                    <td><div>{n.title}</div>{n.body && <div className="sw-hint small">{n.body}</div>}</td>
                    <td>{categoryLabel(n.category)}</td>
                    <td>{priorityLabel(n.priority)}</td>
                    <td>{n.readAt ? formatDateTime(n.readAt) : <span className="sw-hint">Not yet</span>}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </FleetSection>}

      <FleetSection id="nf-tl-deliveries" title="Deliveries">
        {deliveries.length === 0 && <p className="sw-hint m-0">No email or push deliveries: it went to the inbox only, or the event has several recipients (open one from the delivery log).</p>}
        {deliveries.length > 0 &&
          <div className="table-responsive">
            <table className="table nf-table" aria-label="Deliveries">
              <thead><tr><th>Delivery</th><th>Channel</th><th>To</th><th>Provider</th><th>Status</th><th>Attempts</th><th>Last error</th><th /></tr></thead>
              <tbody>
                {deliveries.map(d => {
                  const capture = captureOf(d.id)
                  return (
                    <tr key={d.id}>
                      <td className="sw-mono">{d.id}{d.resentFrom ? <div className="sw-hint small">{`resent from ${d.resentFrom}`}</div> : null}</td>
                      <td>{channelLabel(d.channel)}</td>
                      <td>{d.addressMasked}</td>
                      <td>{providerLabel(d.provider)}</td>
                      <td><Pill tone={deliveryStatusTone(d.status)} title={d.skipReason || undefined}>{deliveryStatusLabel(d.status)}</Pill></td>
                      <td>{d.attempts}</td>
                      <td className="sw-mono small">{d.lastError || ''}{system && d.providerStatus && d.providerStatus !== d.lastError ? <div className="sw-hint">{d.providerStatus}</div> : null}</td>
                      <td className="text-nowrap">
                        {d.emailMessageId && <Link className="me-2" to={`${emailBase}?message=${d.emailMessageId}`}>Email activity</Link>}
                        {capture && <Link className="me-2" to={capturePath(capture.id)}>{`Open capture ${capture.id}`}</Link>}
                        {canAct && RESENDABLE.includes(d.status) &&
                          <button type="button" className="btn btn-sm sw-btn" aria-label={`Resend delivery ${d.id}`} onClick={() => { setActionError(null); setAction({ kind: 'resend', delivery: d }) }}>Resend</button>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>}
      </FleetSection>

      {attempts.length > 0 &&
        <FleetSection id="nf-tl-attempts" title="Attempts">
          <div className="table-responsive">
            <table className="table nf-table" aria-label="Attempts">
              <thead><tr><th>When</th><th>Delivery</th><th>Outcome</th><th>Provider</th><th>HTTP</th><th>Provider said</th><th>Took</th><th>Error</th></tr></thead>
              <tbody>
                {attempts.map(a =>
                  <tr key={a.id}>
                    <td>{formatDateTime(a.attemptedAt)}</td>
                    <td className="sw-mono">{a.deliveryId}</td>
                    <td><Pill tone={outcomeTone(a.outcome)}>{outcomeLabel(a.outcome)}</Pill></td>
                    <td>{providerLabel(a.provider)}{system && a.host ? <div className="sw-hint small sw-mono">{a.host}</div> : null}</td>
                    <td className="sw-mono">{a.httpStatus || ''}</td>
                    <td className="sw-mono">{a.providerStatus || ''}</td>
                    <td>{ms(a.durationMs)}</td>
                    <td className="sw-mono small">{a.error || ''}{a.retryAfterMs ? <div className="sw-hint">{`retry after ${ms(a.retryAfterMs)}`}</div> : null}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </FleetSection>}

      <ReasonDialog
        open={Boolean(action)}
        title={!action ? '' : action.kind === 'resend' ? 'Resend this delivery?' : action.kind === 'replay' ? 'Replay this event?' : 'Park this event?'}
        body={!action ? '' : action.kind === 'resend'
          ? `Delivery ${action.delivery.id} is sent again as a new delivery. This is recorded.`
          : action.kind === 'replay'
            ? 'The event is processed again from the start. Notifications and deliveries already made are not repeated. This is recorded.'
            : "The event stops being retried. Nothing more is sent for it until someone replays it. This is recorded."}
        confirmText={!action ? '' : action.kind === 'resend' ? 'Resend' : action.kind === 'replay' ? 'Replay' : 'Park'}
        danger={Boolean(action && action.kind === 'park')}
        busy={busy}
        error={actionError}
        failure={actionFailure}
        onConfirm={run}
        onCancel={() => setAction(null)}
      />
    </div>
  )
}
